import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { AdminSongPreviewButton } from "../components/AdminSongPreviewButton";
import { AdminSongMetaEditor, bingoCompatibilityBadges } from "../components/AdminSongMetaEditor";
import { useAdminSongPreview } from "../hooks/useAdminSongPreview";

interface Song {
  id: string;
  title: string;
  artist: string;
  releaseYear: number;
  sourceType: "LOCAL_AUDIO" | "REMOTE_AUDIO" | "YOUTUBE";
  sourceValue: string;
  clipStartSeconds: number;
  clipDurationSeconds: number;
  coverImageUrl: string | null;
  enabled: boolean;
  tags: string | null;
  artistType: string | null;
  genre: string | null;
  country: string | null;
  language: string | null;
  createdAt: string;
}

type SortOrder = "year" | "artist" | "title" | "add-asc" | "add-desc";

function compareSongs(a: Song, b: Song, sortOrder: SortOrder): number {
  if (sortOrder === "year") {
    return a.releaseYear - b.releaseYear
      || a.artist.localeCompare(b.artist, "fr")
      || a.title.localeCompare(b.title, "fr");
  }
  if (sortOrder === "artist") {
    return a.artist.localeCompare(b.artist, "fr")
      || a.title.localeCompare(b.title, "fr")
      || a.releaseYear - b.releaseYear;
  }
  if (sortOrder === "title") {
    return a.title.localeCompare(b.title, "fr")
      || a.artist.localeCompare(b.artist, "fr")
      || a.releaseYear - b.releaseYear;
  }
  if (sortOrder === "add-asc") {
    return parseUtcDate(a.createdAt).getTime() - parseUtcDate(b.createdAt).getTime();
  }
  return parseUtcDate(b.createdAt).getTime() - parseUtcDate(a.createdAt).getTime();
}

function parseUtcDate(iso: string): Date {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(iso)) {
    return new Date(`${iso.replace(" ", "T")}Z`);
  }
  return new Date(iso);
}

const parisDateTimeFormatter = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function toParisDateKey(iso: string): string {
  return parseUtcDate(iso).toLocaleDateString("fr-CA", { timeZone: "Europe/Paris" });
}

function formatParisDateLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year}`;
}

function formatAddedAt(iso: string): string {
  return parisDateTimeFormatter.format(parseUtcDate(iso));
}

function hasMusicGuessTag(song: Song): boolean {
  return song.tags?.split(",").some((t) => t.trim() === "MusicGuess") ?? false;
}

function computeCatalogStats(songs: Song[]) {
  const years = songs.map((s) => s.releaseYear);
  const artists = new Set(songs.map((s) => s.artist.trim().toLowerCase()));
  const yearCounts = new Map<number, number>();

  for (const song of songs) {
    yearCounts.set(song.releaseYear, (yearCounts.get(song.releaseYear) ?? 0) + 1);
  }

  const uniqueYears = yearCounts.size;
  const minYear = years.length ? Math.min(...years) : 0;
  const maxYear = years.length ? Math.max(...years) : 0;
  const decadeCounts = new Map<number, number>();

  for (const [year, count] of yearCounts) {
    const decade = Math.floor(year / 10) * 10;
    decadeCounts.set(decade, (decadeCounts.get(decade) ?? 0) + count);
  }

  let topDecade: { label: string; count: number } | null = null;
  for (const [decade, count] of decadeCounts) {
    if (!topDecade || count > topDecade.count) {
      topDecade = { label: `${decade}s`, count };
    }
  }

  let topYear: { year: number; count: number } | null = null;
  for (const [year, count] of yearCounts) {
    if (!topYear || count > topYear.count) {
      topYear = { year, count };
    }
  }

  return {
    total: songs.length,
    musicGuess: songs.filter(hasMusicGuessTag).length,
    uniqueYears,
    minYear,
    maxYear,
    span: maxYear - minYear,
    uniqueArtists: artists.size,
    avgPerYear: uniqueYears ? Math.round((songs.length / uniqueYears) * 10) / 10 : 0,
    topDecade,
    topYear,
  };
}

function buildFilterOptions(songs: Song[]) {
  const decades = new Set<number>();
  const years = new Set<number>();
  const artists = new Set<string>();
  const addedDates = new Map<string, number>();

  for (const song of songs) {
    decades.add(Math.floor(song.releaseYear / 10) * 10);
    years.add(song.releaseYear);
    artists.add(song.artist);
    const dateKey = toParisDateKey(song.createdAt);
    addedDates.set(dateKey, (addedDates.get(dateKey) ?? 0) + 1);
  }

  return {
    decades: [...decades].sort((a, b) => a - b),
    years: [...years].sort((a, b) => a - b),
    artists: [...artists].sort((a, b) => a.localeCompare(b, "fr")),
    addedDates: [...addedDates.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, count]) => ({ dateKey, label: formatParisDateLabel(dateKey), count })),
  };
}

function buildAddOrderMap(songs: Song[]): Map<string, number> {
  const sorted = [...songs].sort(
    (a, b) => parseUtcDate(a.createdAt).getTime() - parseUtcDate(b.createdAt).getTime(),
  );
  const map = new Map<string, number>();
  sorted.forEach((song, index) => map.set(song.id, index + 1));
  return map;
}

export function AdminSongsPage() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [filterDecade, setFilterDecade] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterArtist, setFilterArtist] = useState("");
  const [filterAddedDate, setFilterAddedDate] = useState("");
  const [filterMusicGuess, setFilterMusicGuess] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("year");
  const [editingId, setEditingId] = useState<string | null>(null);
  const preview = useAdminSongPreview();
  const stats = useMemo(() => computeCatalogStats(songs), [songs]);
  const filterOptions = useMemo(() => buildFilterOptions(songs), [songs]);
  const addOrderById = useMemo(() => buildAddOrderMap(songs), [songs]);

  const availableYears = useMemo(() => {
    if (!filterDecade) return filterOptions.years;
    const decade = Number(filterDecade);
    return filterOptions.years.filter((year) => Math.floor(year / 10) * 10 === decade);
  }, [filterDecade, filterOptions.years]);

  const filteredSongs = useMemo(() => {
    const filtered = songs.filter((song) => {
      if (filterDecade && Math.floor(song.releaseYear / 10) * 10 !== Number(filterDecade)) return false;
      if (filterYear && song.releaseYear !== Number(filterYear)) return false;
      if (filterArtist && song.artist !== filterArtist) return false;
      if (filterAddedDate && toParisDateKey(song.createdAt) !== filterAddedDate) return false;
      if (filterMusicGuess === "yes" && !hasMusicGuessTag(song)) return false;
      if (filterMusicGuess === "no" && hasMusicGuessTag(song)) return false;
      return true;
    });

    return filtered.sort((a, b) => compareSongs(a, b, sortOrder));
  }, [songs, filterDecade, filterYear, filterArtist, filterAddedDate, filterMusicGuess, sortOrder]);

  const hasActiveFilters = Boolean(
    filterDecade || filterYear || filterArtist || filterAddedDate || filterMusicGuess || sortOrder !== "year",
  );

  const load = useCallback(async () => {
    const me = await fetch("/api/admin/me", { credentials: "include" });
    if (!me.ok || !(await me.json()).authenticated) {
      window.location.href = "/admin";
      return;
    }
    const res = await fetch("/api/admin/songs", { credentials: "include" });
    if (res.ok) setSongs(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const previewSong = async (song: Song) => {
    preview.setError(null);
    try {
      await preview.playFromApi(song.id);
    } catch (err) {
      preview.setError(err instanceof Error ? err.message : "Lecture impossible");
    }
  };

  const resetFilters = () => {
    setFilterDecade("");
    setFilterYear("");
    setFilterArtist("");
    setFilterAddedDate("");
    setFilterMusicGuess("");
    setSortOrder("year");
  };

  const handleDecadeChange = (value: string) => {
    setFilterDecade(value);
    if (value && filterYear && Math.floor(Number(filterYear) / 10) * 10 !== Number(value)) {
      setFilterYear("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-16">
      <div className="flex items-center justify-between">
        <h1 className="cw-title text-3xl font-bold">Gestion des chansons</h1>
        <Link to="/" className="text-violet-400 hover:text-violet-200">← Retour au jeu</Link>
      </div>

      {preview.error && <p className="p-3 rounded bg-red-500/20 text-red-200">{preview.error}</p>}

      <section className="cw-panel p-6 space-y-6">
        <h2 className="cw-title text-xl font-semibold">Statistiques du catalogue</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: "Chansons", value: stats.total, accent: "text-orange-400" },
            { label: "MusicGuess", value: stats.musicGuess, accent: "text-violet-400" },
            { label: "Artistes", value: stats.uniqueArtists, accent: "text-violet-200" },
            { label: "Années distinctes", value: stats.uniqueYears, accent: "text-orange-400" },
            { label: "Moyenne / année", value: stats.avgPerYear, accent: "text-violet-200" },
            { label: "Période", value: stats.total ? `${stats.minYear} – ${stats.maxYear}` : "—", accent: "text-violet-200" },
            { label: "Étendue", value: stats.total ? `${stats.span} ans` : "—", accent: "text-violet-200" },
          ].map((item) => (
            <div key={item.label} className="rounded-lg bg-violet-950/40 border border-violet-500/15 p-4">
              <p className="text-xs text-violet-300/60 uppercase tracking-wide">{item.label}</p>
              <p className={`text-2xl font-bold mt-1 ${item.accent}`}>{item.value}</p>
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-violet-950/30 border border-violet-500/10 p-4 space-y-2 text-sm">
          <h3 className="font-semibold text-violet-200">Records</h3>
          {stats.topYear && (
            <p>
              <span className="text-violet-300/70">Année la plus fournie :</span>{" "}
              <span className="text-orange-400">{stats.topYear.year}</span> ({stats.topYear.count} titres)
            </p>
          )}
          {stats.topDecade && (
            <p>
              <span className="text-violet-300/70">Décennie dominante :</span>{" "}
              <span className="text-orange-400">{stats.topDecade.label}</span> ({stats.topDecade.count} titres)
            </p>
          )}
        </div>
      </section>

      <div className="cw-panel overflow-hidden">
        <div className="p-4 border-b border-violet-500/10 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="cw-title text-lg font-semibold">Catalogue</h2>
            <p className="text-sm text-violet-300/70">
              {filteredSongs.length} chanson{filteredSongs.length > 1 ? "s" : ""} affichée{filteredSongs.length > 1 ? "s" : ""}
              {hasActiveFilters ? ` sur ${songs.length}` : ""}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3">
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Décennie</span>
              <select
                className="cw-input mt-1"
                value={filterDecade}
                onChange={(e) => handleDecadeChange(e.target.value)}
              >
                <option value="">Toutes</option>
                {filterOptions.decades.map((decade) => (
                  <option key={decade} value={decade}>{decade}s</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Année (sortie)</span>
              <select
                className="cw-input mt-1"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              >
                <option value="">Toutes</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Artiste</span>
              <select
                className="cw-input mt-1"
                value={filterArtist}
                onChange={(e) => setFilterArtist(e.target.value)}
              >
                <option value="">Tous</option>
                {filterOptions.artists.map((artist) => (
                  <option key={artist} value={artist}>{artist}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Date d&apos;ajout</span>
              <select
                className="cw-input mt-1"
                value={filterAddedDate}
                onChange={(e) => setFilterAddedDate(e.target.value)}
              >
                <option value="">Toutes</option>
                {filterOptions.addedDates.map(({ dateKey, label, count }) => (
                  <option key={dateKey} value={dateKey}>{label} ({count})</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Tag MusicGuess</span>
              <select
                className="cw-input mt-1"
                value={filterMusicGuess}
                onChange={(e) => setFilterMusicGuess(e.target.value)}
              >
                <option value="">Toutes</option>
                <option value="yes">MusicGuess uniquement</option>
                <option value="no">Sans MusicGuess</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-violet-300/60 uppercase tracking-wide">Tri</span>
              <select
                className="cw-input mt-1"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              >
                <option value="year">Année</option>
                <option value="artist">Artiste</option>
                <option value="title">Titre</option>
                <option value="add-asc">Ordre d&apos;ajout (1 → {songs.length || "…"})</option>
                <option value="add-desc">Ordre d&apos;ajout ({songs.length || "…"} → 1)</option>
              </select>
            </label>
            <div className="flex items-end">
              <button
                type="button"
                className="cw-btn cw-btn-secondary w-full"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[64rem]">
            <thead className="bg-violet-900/30">
              <tr>
                <th className="p-3 w-12 text-center">#</th>
                <th className="p-3 text-left">Titre</th>
                <th className="p-3 text-left">Artiste</th>
                <th className="p-3">Année</th>
                <th className="p-3 text-left">Tags</th>
                <th className="p-3 text-left">Ajoutée le</th>
                <th className="p-3 text-left">Bingo</th>
                <th className="p-3">Écouter</th>
              </tr>
            </thead>
            <tbody>
              {filteredSongs.map((s) => (
                <Fragment key={s.id}>
                <tr
                  className={`border-t border-violet-500/10 hover:bg-white/5 ${
                    preview.playingId === s.id ? "bg-orange-500/10" : ""
                  }`}
                >
                  <td className="p-3 text-center tabular-nums text-violet-300/70 font-medium">
                    {addOrderById.get(s.id) ?? "—"}
                  </td>
                  <td className="p-3 font-medium">{s.title}</td>
                  <td className="p-3">{s.artist}</td>
                  <td className="p-3 text-center text-orange-400 tabular-nums">{s.releaseYear}</td>
                  <td className="p-3">
                    {hasMusicGuessTag(s) ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-violet-500/20 text-violet-300 border border-violet-500/30">
                        MusicGuess
                      </span>
                    ) : (
                      <span className="text-violet-400/30">—</span>
                    )}
                  </td>
                  <td className="p-3 text-violet-200/80 tabular-nums whitespace-nowrap">
                    {formatAddedAt(s.createdAt)}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {bingoCompatibilityBadges(s).map((b) => (
                        <span
                          key={b.label}
                          className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${
                            b.ok
                              ? "bg-green-500/15 text-green-300"
                              : "bg-yellow-500/15 text-yellow-300"
                          }`}
                        >
                          {b.ok ? "✅" : "⚠️"} {b.label}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="text-xs text-violet-400 underline mt-1"
                      onClick={() => setEditingId(editingId === s.id ? null : s.id)}
                    >
                      {editingId === s.id ? "Fermer" : "Métadonnées"}
                    </button>
                  </td>
                  <td className="p-3 text-center">
                    <AdminSongPreviewButton
                      songId={s.id}
                      playing={preview.playingId === s.id}
                      loading={preview.loadingId === s.id}
                      onClick={() => void previewSong(s)}
                    />
                  </td>
                </tr>
                {editingId === s.id && (
                  <tr>
                    <td colSpan={8} className="p-0">
                      <AdminSongMetaEditor
                        song={s}
                        onSaved={(updated) => {
                          setSongs((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                          setEditingId(null);
                        }}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {songs.length === 0 && <p className="p-8 text-center text-violet-400/50">Aucune chanson</p>}
        {songs.length > 0 && filteredSongs.length === 0 && (
          <p className="p-8 text-center text-violet-400/50">Aucun résultat pour ces filtres</p>
        )}
      </div>
    </div>
  );
}
